import ErrorHandler from "../error-handlers/axios/defaultErrorHandler";

/**
 * Calculates the difference in months between two dates.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {number} The difference in months.
 */
const monthDiff = (date1, date2) => {
  let months;
  months = (date2.getFullYear() - date1.getFullYear()) * 12;
  months -= date1.getMonth();
  months += date2.getMonth();
  return months <= 0 ? 0 : months;
};

/**
 * Fetches historical performance data for a specific agent
 *
 * @param {Object} axiosInstance - Axios instance with authentication
 * @param {string} agentId - The ID of the agent to fetch performance history for
 * @param {Object} options - Request options
 * @param {Object} options.params - Query parameters
 * @param {string} [options.params.period] - Time period ('daily', 'weekly', 'monthly', 'quarterly', 'yearly') - will be overridden by date difference logic if startDate and endDate are provided.
 * @param {string} options.params.startDate - Start date in ISO format
 * @param {string} options.params.endDate - End date in ISO format
 * @param {number} options.params.limit - Number of records to return
 * @returns {Promise<Object>} - Agent performance history data
 */
export const getAgentPerformanceHistory = async (axiosInstance, agentId, options = {}) => {
  try {
    let period = options.params?.period || 'daily'; // Default to daily or provided period

    const { startDate, endDate, ...otherParams } = options.params ?? {};

    if (startDate && endDate) {
      const d1 = new Date(startDate);
      const d2 = new Date(endDate);

      if (d1.toString() !== "Invalid Date" && d2.toString() !== "Invalid Date") {
        const diffInMonths = monthDiff(d1, d2);

        if (diffInMonths > 12) {
          period = 'monthly';
        } else if (diffInMonths > 2) {
          period = 'weekly';
        }
        // If less than or equal to 2 months, 'daily' is implicitly chosen if no period was initially set or if it was 'daily'.
        // If a specific period like 'quarterly' or 'yearly' was passed and dates are within 2 months, that specific period would be overridden to 'daily' by this logic.
        // If you want to preserve other specific periods (e.g. 'quarterly') when the date range is small, adjust the default 'period' assignment or the conditions.
        else if (diffInMonths <= 2) { // only default to daily if no period was specified and conditions for weekly/monthly not met.
          period = 'daily';
        } else if (options.params?.period) {
          period = options.params.period; // retain originally passed period if not overridden by date diff
        }


      } else {
        console.warn("Invalid startDate or endDate provided. Defaulting period logic might not be accurate.");
      }
    }

    const res = await axiosInstance.get(`/agent-performance/history/${agentId}`, {
      params: {
        ...otherParams, // Use otherParams that exclude startDate and endDate which are handled separately
        startDate,    // Add them back here
        endDate,
        period: period  // Use the dynamically determined period
      }
    });
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    return { data: [], error: true };
  }
};